// mount(): builds the panel, subscribes the renderer to the store and
// forwards user actions to the chat controller. The loader owns the
// bubble and the host element; this owns everything inside the panel.

import { trapFocus } from './a11y'
import type { Api } from './api'
import { createChat, fileProblem, type Chat } from './chat'
import {
  buildAttachmentChip,
  buildCsatCard,
  buildCsatThanks,
  buildDayDivider,
  buildHistoryList,
  buildIdentityForm,
  buildNotice,
  buildPanel,
  buildResolvedActions,
  buildTyping,
  buildWelcome,
} from './panel'
import { renderMessage, renderPending, timeAgo } from './render'
import type { SessionManager } from './session'
import { createStore, initialState, type State } from './store'
import type { Theme, WidgetOptions } from './types'

export interface MountHooks {
  onClose(): void
  onResolved(): void
  onIdentityExpired(): void
}

export interface MountHandle {
  open(): void
  close(): void
  isOpen(): boolean
  /** Throws IdentityError when the server rejects the JWT. */
  identify(jwt: string | null): Promise<void>
  /** Live changes from the admin preview. */
  setTheme(patch: Partial<Pick<Theme, 'greeting' | 'title' | 'logoUrl'>>): void
  destroy(): void
}

const ERROR_VISIBLE_MS = 5000
const STATUS = {
  idle: { text: 'Replies right away', tone: 'badge-neutral' },
  answering: { text: 'airys is answering', tone: 'badge-success' },
  human: { text: 'A person is helping you', tone: 'badge-accent' },
  resolved: { text: 'Resolved', tone: 'badge-success' },
} as const

export function mount(
  root: ShadowRoot,
  opts: WidgetOptions,
  theme: Theme,
  deps: { api: Api; session: SessionManager },
  hooks: MountHooks,
): MountHandle {
  const panel = buildPanel(theme)
  panel.root.hidden = true
  root.appendChild(panel.root)

  // `ask` projects: the composer waits behind a name and an email. The
  // form sits where the composer does, so the panel keeps its shape.
  let askingIdentity = deps.session.needsVisitor()
  const identity = buildIdentityForm((visitor) => {
    deps.session.setVisitor(visitor)
    askingIdentity = false
    render(store.get(), store.get())
    panel.textarea.focus()
  })
  panel.composer.before(identity.root)

  const store = createStore(initialState(deps.session.isVerified()))
  const chat: Chat = createChat({
    api: deps.api,
    session: deps.session,
    store,
    preview: opts.preview,
    getJwt: () => opts.userJwt,
    hooks: { onResolved: hooks.onResolved, onIdentityExpired: hooks.onIdentityExpired },
  })

  // ── Transcript: keyed children, reconciled in place ─────────────────

  const nodes = new Map<string, HTMLElement>()
  const typing = buildTyping()

  function transcriptPlan(s: State): [string, () => HTMLElement][] {
    const plan: [string, () => HTMLElement][] = []
    switch (s.phase) {
      case 'welcome':
        if (s.notice) plan.push([`notice:${s.notice}`, () => buildNotice(s.notice!)])
        plan.push([`welcome:${theme.greeting}`, () => buildWelcome(theme.greeting)])
        break
      case 'loading':
        plan.push(['typing', () => typing])
        break
      case 'chat': {
        if (s.notice) plan.push([`notice:${s.notice}`, () => buildNotice(s.notice!)])
        let day = ''
        for (const m of s.messages) {
          const d = m.createdAt.slice(0, 10)
          if (d !== day) {
            day = d
            plan.push([`day:${d}`, () => buildDayDivider(m.createdAt)])
          }
          plan.push([`m:${m.id}`, () => renderMessage(m, theme.helpCenterUrl, theme.title)])
        }
        if (s.pending) {
          const p = s.pending
          plan.push([`pending:${p.localId}`, () => renderPending(p.localId, p.content, p.previewUrl)])
        }
        if (s.awaitingReply) plan.push(['typing', () => typing])
        if (s.conversation?.status === 'resolved') {
          plan.push(
            s.csatSubmitted
              ? ['csat:done', buildCsatThanks]
              : ['csat', () => buildCsatCard((ok) => void chat.submitCsat(ok))],
          )
          plan.push(['resolved', () => buildResolvedActions(() => chat.startNew())])
        }
        break
      }
      case 'history-list': {
        const key = s.history ? s.history.map((c) => c.id).join(',') : 'loading'
        plan.push([`history:${key}`, () => buildHistoryList(s.history, (id) => void chat.openHistoryDetail(id))])
        break
      }
      case 'history-detail': {
        let day = ''
        for (const m of s.historyDetail?.messages ?? []) {
          const d = m.createdAt.slice(0, 10)
          if (d !== day) {
            day = d
            plan.push([`hday:${d}`, () => buildDayDivider(m.createdAt)])
          }
          plan.push([`h:${m.id}`, () => renderMessage(m, theme.helpCenterUrl, theme.title)])
        }
        break
      }
    }
    return plan
  }

  // Appends what is new, removes what is gone, leaves the rest untouched
  // so images and selections survive a refetch. Returns true when
  // something was added at the end.
  function syncTranscript(s: State): boolean {
    const plan = transcriptPlan(s)
    const wanted = new Set(plan.map(([key]) => key))
    for (const [key, node] of nodes) {
      if (!wanted.has(key)) {
        node.remove()
        nodes.delete(key)
      }
    }
    let appended = false
    plan.forEach(([key, make], i) => {
      let node = nodes.get(key)
      if (!node) {
        node = make()
        nodes.set(key, node)
        appended = true
      }
      if (panel.transcript.children[i] !== node) {
        panel.transcript.insertBefore(node, panel.transcript.children[i] ?? null)
      }
    })
    return appended
  }

  function scrollToBottom() {
    requestAnimationFrame(() => {
      panel.transcript.scrollTop = panel.transcript.scrollHeight
    })
  }

  // ── Everything else the store drives ────────────────────────────────

  let errorTimer: number | undefined

  function render(next: State, prev: State) {
    // Header
    const status =
      next.conversation?.status === 'resolved'
        ? STATUS.resolved
        : next.awaitingReply
          ? STATUS.answering
          : next.conversation?.takeoverActive
            ? STATUS.human
            : STATUS.idle
    panel.sub.textContent = status.text
    panel.sub.className = `badge ${status.tone}`
    const onChat = next.phase === 'welcome' || next.phase === 'loading' || next.phase === 'chat'
    panel.backBtn.hidden = onChat
    panel.historyBtn.hidden = !next.verified || !onChat
    panel.title.textContent = onChat ? theme.title : 'Past conversations'
    panel.sub.hidden = !onChat

    // Composer, or the form standing in front of it
    const canWrite =
      (next.phase === 'welcome' || next.phase === 'chat') &&
      (!next.conversation || next.conversation.status === 'active')
    const gated = askingIdentity && canWrite && !next.conversation
    identity.root.hidden = !gated
    panel.composer.hidden = !canWrite || gated
    panel.sendBtn.disabled = !canWrite || !!next.pending
    panel.attachBtn.disabled = !canWrite || !!next.pending

    // Error banner
    if (next.error && next.error !== prev.error) {
      panel.error.textContent = next.error
      panel.error.hidden = false
      window.clearTimeout(errorTimer)
      errorTimer = window.setTimeout(() => {
        panel.error.hidden = true
        store.set({ error: null })
      }, ERROR_VISIBLE_MS)
    } else if (!next.error) {
      panel.error.hidden = true
    }

    // Transcript
    panel.transcript.setAttribute('aria-busy', String(next.phase === 'loading'))
    const appended = syncTranscript(next)
    if (appended && (next.phase === 'chat' || next.phase === 'history-detail')) scrollToBottom()

    // Announcements: replies that arrived while the panel was already showing.
    if (next.phase === 'chat' && prev.phase === 'chat') {
      const known = new Set(prev.messages.map((m) => m.id))
      const fresh = next.messages.filter((m) => m.role !== 'user' && !known.has(m.id))
      if (fresh.length) panel.liveRegion.textContent = fresh.map((m) => m.content).join(' ')
    }
    if (next.notice && next.notice !== prev.notice) panel.liveRegion.textContent = next.notice
  }

  store.subscribe(render)
  render(store.get(), store.get())

  // "just now" ages without a refetch.
  const clock = window.setInterval(() => {
    for (const t of panel.transcript.querySelectorAll<HTMLTimeElement>('time.msg-time')) {
      t.textContent = timeAgo(t.dateTime)
    }
  }, 60_000)

  // ── User input ──────────────────────────────────────────────────────

  function autosize() {
    panel.textarea.style.height = 'auto'
    panel.textarea.style.height = Math.min(panel.textarea.scrollHeight, 120) + 'px'
    panel.sendBtn.classList.toggle('is-ready', panel.textarea.value.trim().length > 0 || !!attached)
  }

  // One image can wait in the composer until the message goes out.
  let attached: File | null = null
  let attachedPreview: string | null = null
  function setAttachment(file: File | null) {
    if (attachedPreview) URL.revokeObjectURL(attachedPreview)
    attached = file
    attachedPreview = file ? URL.createObjectURL(file) : null
    panel.attachment.replaceChildren()
    panel.attachment.hidden = !file
    if (file && attachedPreview) {
      panel.attachment.appendChild(buildAttachmentChip(attachedPreview, file.name, () => setAttachment(null)))
    }
    panel.sendBtn.classList.toggle('is-ready', panel.textarea.value.trim().length > 0 || !!attached)
  }

  async function submit() {
    const text = panel.textarea.value.trim()
    const file = attached
    if ((!text && !file) || store.get().pending) return
    panel.textarea.value = ''
    setAttachment(null)
    autosize()
    const sent = await chat.send(text, file ?? undefined)
    // Give it all back rather than losing it to a failed request.
    if (!sent) {
      if (!panel.textarea.value) panel.textarea.value = text
      if (file && !attached) setAttachment(file)
      autosize()
    }
  }

  panel.attachBtn.addEventListener('click', () => panel.fileInput.click())
  panel.fileInput.addEventListener('change', () => {
    const file = panel.fileInput.files?.[0] ?? null
    panel.fileInput.value = ''
    if (!file) return
    const problem = fileProblem(file)
    if (problem) {
      store.set({ error: problem })
      return
    }
    setAttachment(file)
    panel.textarea.focus()
  })

  panel.textarea.addEventListener('input', autosize)
  panel.textarea.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void submit()
    }
  })
  panel.sendBtn.addEventListener('click', () => void submit())
  panel.closeBtn.addEventListener('click', () => close())
  panel.historyBtn.addEventListener('click', () => void chat.openHistory())
  // Back walks up: detail → list → chat.
  panel.backBtn.addEventListener('click', () => {
    if (store.get().phase === 'history-detail') void chat.openHistory()
    else chat.backToChat()
  })

  // ── Open / close ────────────────────────────────────────────────────

  let opened = false
  let hydrated = false
  let untrap: (() => void) | null = null
  const onKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') close()
  }

  function open() {
    if (opened) return
    opened = true
    panel.root.hidden = false
    untrap = trapFocus(panel.root)
    panel.root.addEventListener('keydown', onKeydown)
    if (!hydrated) {
      hydrated = true
      void chat.hydrate()
    } else {
      chat.onOpen()
    }
    requestAnimationFrame(() => panel.textarea.focus())
  }

  function close() {
    if (!opened) return
    opened = false
    chat.onClose()
    panel.root.hidden = true
    untrap?.()
    untrap = null
    panel.root.removeEventListener('keydown', onKeydown)
    hooks.onClose()
  }

  return {
    open,
    close,
    isOpen: () => opened,
    identify: (jwt) => chat.identify(jwt),
    setTheme(patch) {
      if (patch.title !== undefined) {
        theme.title = patch.title
        render(store.get(), store.get())
      }
      if (patch.greeting !== undefined) {
        theme.greeting = patch.greeting
        render(store.get(), store.get())
      }
      if (patch.logoUrl !== undefined) {
        theme.logoUrl = patch.logoUrl
        if (patch.logoUrl) panel.logo.src = patch.logoUrl
        panel.logo.hidden = !patch.logoUrl
      }
    },
    destroy() {
      chat.destroy()
      setAttachment(null)
      window.clearTimeout(errorTimer)
      window.clearInterval(clock)
      untrap?.()
      panel.root.remove()
    },
  }
}
